import React, { useState, useCallback } from 'react';

import { BuilderComponent } from '@builder.io/react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import BuilderSizeGuideTabs from '~/builder/components/BuilderSizeGuideTabs';
import { BUILDER_MODELS } from '~/builder/constants/models';
import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import { Button, desktop } from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';

const dialogStyle = css`
  ${desktop`
    max-height: calc(100vh - 128px);
  `}
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 18px;
`;

const modalContentWrapperStyles = css`
  display: unset;
`;

const ModalContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px;
`;

const SizeGuideButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  font-weight: 700;
  font-size: 14px;
  line-height: 2;
  letter-spacing: ${-0.35 / 14}em;
  text-decoration: underline;

  ${({ sizeGuideButtonStyle }) => sizeGuideButtonStyle}
`;

const transitionProps = {
  enter: { opacity: 1, transform: 'scale(1, 1)' },
  from: { opacity: 0, transform: 'scale(0.5, 0.5)' },
  leave: { opacity: 0, transform: 'scale(0.5, 0.5)' },
};

const BuilderSizeGuideModal = ({ builderContent, sizeGuideButtonStyle }) => {
  const intl = useIntl();
  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Extract modal title from Builder content, with fallback
  const modalTitle =
    builderContent?.data?.title ||
    intl.formatMessage({
      id: 'site_pdp.size_guide',
      defaultMessage: 'Size Guide',
    });

  return (
    <>
      <SizeGuideButton
        variant="unstyled"
        onClick={openModal}
        sizeGuideButtonStyle={sizeGuideButtonStyle}
      >
        {builderContent?.data?.triggerLabel ? (
          <span>{builderContent.data.triggerLabel}</span>
        ) : (
          <FormattedMessage
            id="site_pdp.size_guide_link"
            defaultMessage="View Size Guide"
          />
        )}
      </SizeGuideButton>
      <FableticsModal
        contentWrapperStyles={modalContentWrapperStyles}
        dialogStyle={dialogStyle}
        isOpen={isModalOpen}
        onExit={closeModal}
        closeButton={<ModalCloseButton aria-label={closeLabel} />}
        showCloseButton
        transitionEnter={transitionProps.enter}
        transitionFrom={transitionProps.from}
        transitionLeave={transitionProps.leave}
        title={modalTitle}
        variant="unpadded"
      >
        <ModalContent>
          {builderContent?.data?.tabs ? (
            <BuilderSizeGuideTabs
              builderBlock={{ id: builderContent.id }}
              tabs={builderContent.data.tabs}
            />
          ) : (
            <BuilderComponent
              model={BUILDER_MODELS.SIZE_GUIDE}
              content={builderContent}
            />
          )}
        </ModalContent>
      </FableticsModal>
    </>
  );
};

BuilderSizeGuideModal.propTypes = {
  builderContent: PropTypes.object.isRequired,
  sizeGuideButtonStyle: PropTypes.any,
};

export default BuilderSizeGuideModal;
